import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '../api/endpoints';
import { useNotification } from '../context/NotificationContext';
import { formatTimeAgo } from '../utils/helpers';
import { FaBell, FaCheck, FaTimes, FaTrash, FaSpinner } from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './Notifications.css';

const Notifications = () => {
  const navigate = useNavigate();
  const { setUnreadCount } = useNotification();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 0, current: 1 });
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchNotifications();
  }, [pagination.current, filter]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const params = {
        limit: 20,
        offset: (pagination.current - 1) * 20,
        read: filter === 'all' ? null : filter === 'unread' ? 0 : 1,
      };
      const response = await notificationAPI.getList(params);
      setNotifications(response.data.data || []);
      setPagination({
        total: response.data.total || 0,
        pages: Math.ceil(response.data.total / 20),
        current: pagination.current,
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationAPI.markAsRead(id);
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, read: 1 } : n)
      );
      // Update unread count
      const countRes = await notificationAPI.getUnreadCount();
      setUnreadCount(countRes.data.unread_count || 0);
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications(prev => 
        prev.map(n => ({ ...n, read: 1 }))
      );
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleDelete = async (id) => {
    try {
      await notificationAPI.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      toast.success('Notification deleted');
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      bet_marked: '📌',
      bet_code_created: '📢',
      prediction_result: '🎯',
      h2h_result: '🏆',
      competition_update: '📊',
      subscription_update: '📰',
      wallet_update: '💰',
      rollover_update: '🔄',
      referral_bonus: '🎁',
      creator_subscription: '⭐',
      private_bet_notification: '🔒',
      winner_notification: '👑',
    };
    return icons[type] || '📬';
  };

  if (loading && pagination.current === 1) {
    return (
      <div className="notifications-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return n.read === 0;
    if (filter === 'read') return n.read === 1;
    return true;
  });

  return (
    <div className="notifications-page">
      <div className="notifications-header">
        <div className="header-left">
          <h1><FaBell className="header-icon" /> Notifications</h1>
          {pagination.total > 0 && (
            <span className="total-badge">{pagination.total} total</span>
          )}
        </div>
        <div className="header-actions">
          {notifications.some(n => n.read === 0) && (
            <button onClick={handleMarkAllAsRead} className="btn btn-secondary btn-sm">
              <FaCheck /> Mark all read
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="notification-filters">
        <button 
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button 
          className={`filter-btn ${filter === 'unread' ? 'active' : ''}`}
          onClick={() => setFilter('unread')}
        >
          Unread
        </button>
        <button 
          className={`filter-btn ${filter === 'read' ? 'active' : ''}`}
          onClick={() => setFilter('read')}
        >
          Read
        </button>
      </div>

      {/* Notifications List */}
      {filteredNotifications.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔔</div>
          <h3>No notifications</h3>
          <p>You're all caught up!</p>
        </div>
      ) : (
        <div className="notifications-list">
          {filteredNotifications.map((notification) => (
            <div 
              key={notification.id} 
              className={`notification-item ${notification.read === 0 ? 'unread' : ''}`}
            >
              <div className="notification-icon">
                {getTypeIcon(notification.type)}
              </div>
              <div className="notification-content">
                {notification.title && (
                  <div className="notification-title">{notification.title}</div>
                )}
                <p className="notification-message">{notification.message}</p>
                <span className="notification-time">
                  {formatTimeAgo(notification.created_at)}
                </span>
              </div>
              <div className="notification-actions">
                {notification.read === 0 && (
                  <button 
                    onClick={() => handleMarkAsRead(notification.id)}
                    className="action-btn mark-read"
                    title="Mark as read"
                  >
                    <FaCheck />
                  </button>
                )}
                {notification.link && (
                  <button 
                    onClick={() => navigate(notification.link)}
                    className="action-btn view"
                    title="View"
                  >
                    👁️
                  </button>
                )}
                <button 
                  onClick={() => handleDelete(notification.id)}
                  className="action-btn delete"
                  title="Delete"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
            disabled={pagination.current === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {pagination.current} of {pagination.pages}
          </span>
          <button 
            onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
            disabled={pagination.current === pagination.pages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Notifications;